/**
 * Character service — a user's reusable Media Studio "character sheets" (CRUD). A character locks a
 * look from an existing in-chat image (referenced by Storage path or Drive file id, never re-uploaded)
 * plus a fixed seed, so the same face can be animated (image-to-video) and reused across clips.
 */
import { callFunction } from '../utils/apiClient';

export interface Character {
  id: string;
  owner_id: string;
  name: string;
  reference_path: string | null;
  /** Extra angles (three-quarter, side) of the same face — a model sheet that locks identity across shots. */
  turnaround_paths?: string[];
  reference_drive_id: string | null;
  reference_mime: string;
  appearance: string;
  seed: number;
  /** The series cast-bible this character belongs to (null = standalone). */
  series_id: string | null;
  /** The locked voice this character speaks in across every episode. */
  voice_id: string | null;
  created_at: string;
  updated_at: string;
}

export type CharacterInput = Partial<Omit<Character, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>;

export const listCharacters = () =>
  callFunction<{ characters: Character[] }>('listCharacters', {}).then((r) => r.characters || []);

/** The cast bible for a series — its locked characters (face + voice). */
export const listSeriesCast = (seriesId: string) =>
  callFunction<{ characters: Character[] }>('listSeriesCast', { seriesId }).then((r) => r.characters || []);

export const createCharacter = (input: CharacterInput) =>
  callFunction<Character>('createCharacter', input);

/** Cast a roster specialist as a character — their portrait becomes the locked face, their voice the locked
 *  voice. Idempotent: casting the same agent twice returns the existing sheet. */
export const castAgentAsCharacter = (agentId: string, seriesId?: string | null, name?: string) =>
  callFunction<Character>('castAgentAsCharacter', { agentId, seriesId: seriesId ?? null, ...(name ? { name } : {}) });

/**
 * Generate the character's TURNAROUND (three-quarter + side views) from its locked face. Fire-and-forget from the
 * UI: the character is already usable off its front reference, and the extra angles simply sharpen consistency
 * on the next render. Idempotent server-side, so a repeat call is a cheap no-op.
 */
/** `force` REDRAWS a sheet that already exists — the server reuses by default so a page load never re-bills. */
export const generateCharacterTurnaround = (id: string, force = false) =>
  callFunction<Character>('generateCharacterTurnaround', { id, force });

export const updateCharacter = (id: string, patch: CharacterInput) =>
  callFunction<Character>('updateCharacter', { id, ...patch });

export const deleteCharacter = (id: string) =>
  callFunction<{ deleted: boolean }>('deleteCharacter', { id });
